import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useLastLocation } from 'react-router-last-location';

import { trackEvent, trackPageview } from '@xingternal/hops-tracking';
import { ExperimentTrackingData, useExperimentTrackingData, useViewerData } from '@xingternal/hub';

import { JDPModuleName } from '@/components/job-posting/types';
import { CONFINED_MIN_WIDTH } from '@/config';
import { useHighlightsTrackingData } from '@/domain/job-details/components/header/components/highlights';
import { JobPostingVisibleJobFragment, JobSalary, useRegisterVisitMutation } from '@/graphql/generated';
import { getRevenueTypeContextDimension } from '@/helpers/job-posting-revenue-type';
import { trackNwtEvents } from '@/helpers/nwt-tracking';
import { fromQuery } from '@/helpers/query-string';
import { locationToAbsoluteURL } from '@/helpers/url';
import { unscrambleId } from '@/helpers/xing-one';
import { useLoginStates, useWindowSize } from '@/hooks';

const selectQueryParamsFromLocation = (location): string => location.search.substring(1);

const isValidModuleIndex = (moduleIndex: number): boolean => moduleIndex >= 0;

export type ScrollTrackingEvent = {
  moduleName: JDPModuleName;
  customTrackingProps?: Record<string, string>;
};

const sendScrollTracking = ({
  moduleName,
  moduleIndex,
  customTrackingProps,
}: ScrollTrackingEvent & { moduleIndex: number }): void => {
  isValidModuleIndex(moduleIndex) &&
    trackEvent('PropScrolling', {
      PropScrolling: 'jobs_detail_module',
      PropPostponeAction: 1,
      PropModules: `${moduleName}_${moduleIndex + 1}`,
      ...customTrackingProps,
    });
};

type ScrollTrackingCallback = (trackingData: ScrollTrackingEvent) => void;

const ScrollTrackingContext = createContext<ScrollTrackingCallback | undefined>(undefined);

type UpdateTrackingModuleListCallback = (modules: string[]) => void;

const UpdateTrackingModuleListContext = createContext<UpdateTrackingModuleListCallback | undefined>(undefined);

type JobPostingTrackingProviderProps = { job: JobPostingVisibleJobFragment };

enum SalaryTypeContextDimension {
  Salary = 'jobs_salary',
  SalaryRange = 'jobs_salary_range',
  SalaryEstimate = 'jobs_salary_estimate',
  NoSalary = 'jobs_no_salary',
}

const getSalaryTypeContextDimension = (salary?: JobSalary | null): SalaryTypeContextDimension => {
  const salaryTypeName = salary?.__typename;

  if (!salaryTypeName || SalaryTypeContextDimension[salaryTypeName] === undefined)
    return SalaryTypeContextDimension.NoSalary;

  return SalaryTypeContextDimension[salaryTypeName];
};

export const JobPostingTrackingProvider: React.FC<JobPostingTrackingProviderProps> = ({ job, children }) => {
  const [moduleList, updateModuleList] = useState<string[] | undefined>();
  const [isPageViewTracked, setIsPageViewTracked] = useState<boolean>(false);
  const [slugTracked, setSlugTracked] = React.useState<string>();
  const location = useLocation();
  const lastLocation = useLastLocation();
  const [registerVisitMutation] = useRegisterVisitMutation();
  const scrollEventQueue = useRef<ScrollTrackingEvent[]>([]);
  const { isLoggedIn, isLoggedOut } = useLoginStates();
  const highlightsTrackingData = useHighlightsTrackingData();
  const { data: userData } = useViewerData();
  const userId = userData?.user?.id;
  const unscrambledUserId = userId ? unscrambleId(userId) : 0;

  // Using window size to avoid SSR issues
  // See annotation: https://brewery.xing.com/media-listener
  const { width } = useWindowSize();
  const isMobile = width && width < CONFINED_MIN_WIDTH;

  const experimentTrackingData: ExperimentTrackingData = useExperimentTrackingData();

  const trackScrollEvent = useCallback(
    ({ moduleName, customTrackingProps = {} }: ScrollTrackingEvent): void => {
      if (!isPageViewTracked) {
        scrollEventQueue.current.push({ moduleName, customTrackingProps });
      } else {
        sendScrollTracking({ moduleName, moduleIndex: moduleList?.indexOf(moduleName) || -1, customTrackingProps });
      }
    },
    [isPageViewTracked, moduleList]
  );

  // flush scroll tracking queue
  useEffect(() => {
    if (isPageViewTracked && scrollEventQueue.current.length) {
      scrollEventQueue.current.forEach(({ moduleName, customTrackingProps }) => {
        sendScrollTracking({ moduleName, moduleIndex: moduleList?.indexOf(moduleName) || -1, customTrackingProps });
      });
      scrollEventQueue.current = [];
    }
  }, [isPageViewTracked, moduleList]);

  // track page
  useEffect(() => {
    if (job && moduleList && !isPageViewTracked) {
      const queryParams = selectQueryParamsFromLocation(location);
      const trackSearchPosition = queryParams.includes('ttt_sp');
      const trackActionOrigin = queryParams.includes('sc_o_PropActionOrigin');

      setSlugTracked(job.slug);
      setIsPageViewTracked(true);

      registerVisitMutation({
        variables: {
          id: job.id,
          originalUrl: window.location.href,
          referer: lastLocation ? locationToAbsoluteURL(lastLocation) : document.referrer,
        },
      });

      trackPageview(
        'wbm/Stellenmarkt/detail',
        {
          PropJobsId: job.id,
          PropContextDimension2: getRevenueTypeContextDimension(job),
          PropContextDimension3: getSalaryTypeContextDimension(job.salary),
          PropContextDimension5: 'jobs_jdp_react_new',
          ...(job.discipline && { PropJobsDiscipline: job.discipline?.id }),
          PropModules: moduleList.map((moduleName, position) => `${moduleName}_${position + 1}`).join(','),
          ...(trackSearchPosition && { PropSearchPosition: fromQuery(queryParams).ttt_sp as string }),
          brazeUserId: unscrambledUserId,
        },
        {
          EventJobsDetailView: 1,
          ...(trackActionOrigin && { PropActionOrigin: fromQuery(queryParams).sc_o_PropActionOrigin as string }),
          ...(experimentTrackingData.PropExperiment &&
            experimentTrackingData.PropExperimentInfo &&
            experimentTrackingData),
          ...(highlightsTrackingData && { PropContextAddition: highlightsTrackingData }),
        }
      );

      trackNwtEvents([
        {
          user_id: userId,
          event: 'viewed_screen',
          page: 'jobs/detail',
          item_urn: job.globalId,
          channel: isMobile ? 'webm' : 'web',
        },
        {
          user_id: userId,
          event: 'job_detail_viewed',
          page: 'jobs/detail',
          item_urn: job.globalId,
          channel: isMobile ? 'webm' : 'web',
        },
      ]);
    }
  }, [
    isPageViewTracked,
    job,
    lastLocation,
    location,
    moduleList,
    registerVisitMutation,
    slugTracked,
    unscrambledUserId,
    isLoggedIn,
    isLoggedOut,
    experimentTrackingData,
    userId,
    isMobile,
    highlightsTrackingData,
  ]);

  return (
    <UpdateTrackingModuleListContext.Provider value={updateModuleList}>
      <ScrollTrackingContext.Provider value={trackScrollEvent}>{children}</ScrollTrackingContext.Provider>
    </UpdateTrackingModuleListContext.Provider>
  );
};

export const useJobPostingScrollTracking = (): ScrollTrackingCallback => {
  const trackScrollEvent = useContext(ScrollTrackingContext);
  if (typeof trackScrollEvent === 'undefined') {
    throw new Error('useJobPostingScrollTracking must be used within a JobPostingTrackingProvider');
  }
  return trackScrollEvent;
};

export const useUpdateTrackingModuleList = (): UpdateTrackingModuleListCallback => {
  const updateModuleList = useContext(UpdateTrackingModuleListContext);
  if (typeof updateModuleList === 'undefined') {
    throw new Error('useUpdateTrackingModuleList must be used within a JobPostingTrackingProvider');
  }
  return updateModuleList;
};
